import Box from '@/Atoms/Box';
import React, {useMemo, useState, useEffect} from 'react';
import {
  styles,
  CARD_BANNER_COLORS,
  CARD_ICONS,
  HERO_BG,
  HERO_ACCENT,
  PAGE_BG,
} from './styles';
import {
  ScrollView,
  useWindowDimensions,
  View,
  TextInput,
  TouchableOpacity,
  Clipboard,
  Text,
} from 'react-native';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCButton from '@/Atoms/CCButton';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import CCText from '@/Atoms/CCText';
import {useGetAllStudyGames} from '@/Hooks/StudyGamesListApi/useGetAllStudyGames';
import useSyncStudies from '@/Hooks/StudyGamesListApi/useSyncStudies';
import useSyncStudiesProgress from '@/Hooks/StudyGamesListApi/useSyncStudiesProgress';
import useDeleteStudy from '@/Hooks/StudyGamesListApi/useDeleteStudy';
import useLichessAuthStatus from '@/Hooks/StudyGamesListApi/useLichessAuthStatus';
import ChessRestClient from '@/Api/ChessRestClient';
import {getUserDetails} from '@/Utils/CommonUtils';
import CCLoader from '@/Atoms/CCLoader';
import {formatDate} from '@/Utils/CommonUtils';
import {useNavigation} from '@react-navigation/native';
import {ScreenName} from '@/navigators/StackConstants';
import SlideModal from '@/Atoms/SlideModal';
import CreateNewStudy from './Component/CreateNewStudy';
import ImportLichessStudies from './Component/ImportLichessStudies';
import Empty from '@/Atoms/Empty';
import WorkbookEmptyBook from '@/Assets/Images/Workbook/WorkbookEmptyPage.svg';
import CCLogo from '@/Assets/Images/Svgs/ccIcon.svg';
import LichessLogo from '@/Assets/Images/Svgs/lichess_knight.svg';
import {FontSizes} from '@/Utils/Dimensions';
import {ChessConfirm, ChessModalShell} from '@/Components/ChessModal';
import useUpdateVisibility from '@/Hooks/StudyGamesListApi/useUpdateVisibility';
import useCloneStudyGame from '@/Hooks/StudyGamesListApi/useCloneStudyGame';
import useRenameStudy from '@/Hooks/StudyGamesListApi/useRenameStudy';
import useManageParticipants from '@/Hooks/ManageStudyParticipantsApi/useManageParticipants';
import useUpdateParticipantRole from '@/Hooks/ManageStudyParticipantsApi/useUpdateParticipantRole';
import CCTextInput from '@/Atoms/CCTextInput';
import {useToast} from '@/toast/useToast';

type Tab = 'owner' | 'participant';

const CARD_GAP = 20;

// ─────────────────────────────────────────────────────────────────────────────
// Decorative 64-square chess board mosaic (purely visual)
// ─────────────────────────────────────────────────────────────────────────────
const ChessMosaic = ({
  size = 120,
  opacity = 0.035,
  style = {},
}: {
  size?: number;
  opacity?: number;
  style?: any;
}) => {
  const sq = size / 8;
  return (
    <View
      pointerEvents="none"
      style={[
        {
          flexDirection: 'row',
          flexWrap: 'wrap',
          width: size,
          height: size,
          opacity,
        },
        style,
      ]}>
      {Array.from({length: 64}).map((_, i) => {
        const row = Math.floor(i / 8);
        const col = i % 8;
        const isLight = (row + col) % 2 === 0;
        return (
          <View
            key={i}
            style={{
              width: sq,
              height: sq,
              backgroundColor: isLight ? CCColors.White : 'transparent',
            }}
          />
        );
      })}
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// HeroHeader
// ─────────────────────────────────────────────────────────────────────────────

const HeroHeader = ({
  onCreate,
  onSync,
  workbookCount,
}: {
  onCreate: () => void;
  onSync: () => void;
  workbookCount: number;
}) => (
  <View
    style={{
      backgroundColor: '#150A05',
      backgroundImage: 'linear-gradient(135deg, #2E1B10 0%, #150A05 100%)',
      marginHorizontal: 16,
      marginTop: 16,
      borderRadius: 16,
      paddingHorizontal: 22,
      paddingTop: 18,
      paddingBottom: 18,
      overflow: 'hidden',
      borderWidth: 1.5,
      borderColor: 'rgba(250,207,71,0.15)',
      position: 'relative',
    } as any}>
    {/* top gold accent stripe */}
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        top: 0,
        left: 28,
        right: 28,
        height: 2.5,
        backgroundColor: '#FACF47',
        opacity: 0.6,
      }}
    />
    
    <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', zIndex: 1}}>
      {/* left: breadcrumb + title + stats */}
      <View style={{flex: 1, marginRight: 20}}>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 7}}>
          <MaterialCommunityIcons name="school-outline" size={11} color="rgba(255, 255, 255, 0.45)" />
          <Text style={{fontSize: 10.5, color: 'rgba(255, 255, 255, 0.45)'}}>Training</Text>
          <MaterialCommunityIcons name="chevron-right" size={11} color="rgba(255, 255, 255, 0.3)" />
          <Text style={{fontSize: 10.5, color: 'rgba(255, 255, 255, 0.65)', fontWeight: 'bold'}}>Workbooks</Text>
        </View>

        <View style={{flexDirection: 'row', alignItems: 'center', gap: 10}}>
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              backgroundColor: 'rgba(250,207,71,0.1)',
              borderWidth: 1,
              borderColor: 'rgba(250,207,71,0.25)',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <MaterialCommunityIcons name="book-open-page-variant" size={16} color="#FACF47" />
          </View>
          <View style={{flex: 1}}>
            <Text style={{fontSize: 16.5, fontWeight: 'bold', color: '#FFFFFF', letterSpacing: -0.2, lineHeight: '21px'}}>
              Workbooks
            </Text>
            <Text style={{fontSize: 11, color: 'rgba(255, 255, 255, 0.6)', marginTop: 1}}>
              Your personal chess training library
            </Text>
          </View>
        </View>

        <View style={{flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 11, flexWrap: 'wrap'}}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderWidth: 1,
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 7,
              paddingHorizontal: 9,
              paddingVertical: 4,
              flexShrink: 0,
            }}>
            <MaterialCommunityIcons name="book-multiple-outline" size={11} color="#FACF47" />
            <Text style={{fontSize: 11, color: '#FFFFFF', fontWeight: 'semibold'}}>
              {workbookCount} {workbookCount === 1 ? 'workbook' : 'workbooks'}
            </Text>
          </View>
          <View style={{width: 1, height: 14, backgroundColor: 'rgba(255, 255, 255, 0.12)'}} />
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 5}}>
            <MaterialCommunityIcons name="chess-king" size={12} color="#FACF47" />
            <Text style={{fontSize: 11, color: 'rgba(255, 255, 255, 0.5)'}}>
              Study · Annotate · Share
            </Text>
          </View>
        </View>
      </View>

      {/* right: actions */}
      <View style={{flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 0}}>
        <TouchableOpacity
          activeOpacity={0.8}
          style={styles.heroSyncButton}
          onPress={onSync}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
            <MaterialCommunityIcons name="sync" size={13} color="#FFFFFF" />
            <Text style={{fontSize: 11.5, fontWeight: 'bold', color: '#FFFFFF'}}>
              Sync from Lichess
            </Text>
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          activeOpacity={0.85}
          style={styles.heroCreateButton}
          onPress={onCreate}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
            <MaterialCommunityIcons name="plus" size={14} color="#1E110A" />
            <Text style={{fontSize: 11.5, fontWeight: 'bold', color: '#1E110A'}}>
              New Workbook
            </Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  </View>
);

const SegmentedTabs = ({
  tabSelected,
  onChange,
}: {
  tabSelected: number;
  onChange: (index: number) => void;
}) => (
  <View style={styles.segmentWrapper}>
    {['My Workbooks', 'Shared with me'].map((label, index) => {
      const active = tabSelected === index;
      return (
        <TouchableOpacity
          key={label}
          activeOpacity={0.85}
          style={[styles.segmentItem, active && styles.segmentItemActive]}
          onPress={() => onChange(index)}>
          <Text
            style={{
              fontSize: 12.5,
              fontWeight: active ? 'bold' : '500',
              color: active ? '#FFFFFF' : '#757575',
            }}>
            {label}
          </Text>
        </TouchableOpacity>
      );
    })}
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// WorkbookCard — full redesign
//
// Layout:
//  ┌─────────────────────────────────────────┐
//  │ [#01] Workbook Title          ♟ (piece) │  ← coloured header strip
//  ├─────────────────────────────────────────┤
//  │ 👤  Owner Name                          │  ← owner row
//  │                                         │
//  │  CHAPTERS                               │  ← section label
//  │  ·  Chapter One Name                    │
//  │  ·  Chapter Two Name                    │
//  │  ·  Chapter Three Name                  │
//  │  ·  Chapter Four Name                   │
//  │     + N more                            │  ← if >4
//  ├─────────────────────────────────────────┤
//  │  📅 12 Jun 2025      [🗑 Delete]        │  ← footer
//  └─────────────────────────────────────────┘
// ─────────────────────────────────────────────────────────────────────────────
const WorkbookRow = ({
  name,
  owner,
  createdAt,
  chaptersCount,
  visibility,
  platform,
  index,
  isShared,
  onPress,
  onDelete,
  onShare,
  onClone,
  onEdit,
}: {
  name: string;
  owner?: string;
  createdAt?: string;
  chaptersCount?: number;
  visibility?: string;
  platform?: string;
  index: number;
  isShared: boolean;
  onPress: () => void;
  onDelete?: () => void;
  onShare?: () => void;
  onClone?: (cloneName?: string) => void;
  onEdit?: () => void;
}) => {
  const {isMobileOrTab} = useMediaQuery();
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [isCloneConfirmVisible, setIsCloneConfirmVisible] = useState(false);
  const [cloneName, setCloneName] = useState(() => `Copy of ${name}`);

  useEffect(() => {
    setCloneName(`Copy of ${name}`);
  }, [name]);

  const [isHovered, setIsHovered] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const [isShareHovered, setIsShareHovered] = useState(false);
  const [isEditHovered, setIsEditHovered] = useState(false);
  const [isCombinedHovered, setIsCombinedHovered] = useState(false);
  const [isCloneHovered, setIsCloneHovered] = useState(false);

  const bannerColor = CARD_BANNER_COLORS[index % CARD_BANNER_COLORS.length];

  return (
    <View
      // @ts-ignore
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={[
        styles.listRow,
        isHovered && styles.listRowHover,
        {
          borderLeftWidth: 3,
          borderLeftColor: isHovered ? bannerColor : 'transparent',
        },
      ]}>
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={onPress}
        style={styles.listRowContent}>
        {isMobileOrTab ? (
          // Mobile Layout
          <View style={{flex: 1, gap: 10}}>
            <View style={{flexDirection: 'row', alignItems: 'center', justifyStyle: 'space-between', justifyContent: 'space-between', gap: 10}}>
              <View style={{flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1}}>
                <View style={[styles.listSerialBadge, {backgroundColor: bannerColor}]}>
                  <Text style={styles.listSerialText}>{String(index + 1).padStart(2, '0')}</Text>
                </View>
                <View style={{flex: 1}}>
                  <Text style={styles.listTitleText} numberOfLines={1}>{name}</Text>
                  <Text style={styles.listSubtitleText}>{isShared ? 'Shared' : 'Mine'}</Text>
                </View>
              </View>
              
              {/* Mobile actions */}
              <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                {onEdit && (
                  <TouchableOpacity
                    onPress={(e) => { e.stopPropagation(); onEdit(); }}
                    style={{padding: 6}}>
                    <MaterialCommunityIcons name="pencil-outline" size={16} color="#4E3E34" />
                  </TouchableOpacity>
                )}
                {onClone && (
                  <TouchableOpacity
                    onPress={(e) => { e.stopPropagation(); setIsCloneConfirmVisible(true); setIsDeleteConfirmVisible(false); }}
                    style={{padding: 6}}>
                    <MaterialCommunityIcons name="content-copy" size={15} color="#00B074" />
                  </TouchableOpacity>
                )}
                {onDelete && (
                  <TouchableOpacity
                    onPress={(e) => { e.stopPropagation(); setIsDeleteConfirmVisible(true); setIsCloneConfirmVisible(false); }}
                    style={{padding: 6}}>
                    <MaterialCommunityIcons name="trash-can-outline" size={16} color="#D32F2F" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
            
            {/* Bottom metadata tags */}
            <View style={{flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 10}}>
              <View style={styles.listChaptersBadge}>
                <Text style={styles.listChaptersText}>{chaptersCount ?? 0} chapters</Text>
              </View>
              
              <View style={styles.listPlatformContainer}>
                {platform === 'lichess' ? (
                  <img src={LichessLogo} style={{width: 14, height: 14}} alt="Lichess" />
                ) : (
                  <img src={CCLogo} style={{width: 14, height: 14}} alt="CC" />
                )}
              </View>
              
              <View style={styles.listVisibilityContainer}>
                <MaterialCommunityIcons
                  name={visibility === 'private' ? 'lock-outline' : 'earth'}
                  size={12}
                  color="#4E3E34"
                />
                <Text style={{fontSize: 10.5, color: '#4E3E34'}}>{visibility === 'private' ? 'Private' : 'Public'}</Text>
              </View>
              
              <View style={styles.listDateContainer}>
                <MaterialCommunityIcons name="calendar-month-outline" size={11} color="#757575" />
                <Text style={{fontSize: 10.5, color: '#757575'}}>{formatRelativeTime(createdAt)}</Text>
              </View>
            </View>
          </View>
        ) : (
          // Desktop Layout
          <>
            {/* Serial badge */}
            <View style={{width: 40, justifyContent: 'center'}}>
              <View style={[styles.listSerialBadge, {backgroundColor: bannerColor}]}>
                <Text style={styles.listSerialText}>{String(index + 1).padStart(2, '0')}</Text>
              </View>
            </View>

            {/* Workbook Name & Subtitle */}
            <View style={{flex: 2.2, justifyContent: 'center', gap: 2, paddingRight: 10}}>
              <Text style={styles.listTitleText} numberOfLines={1}>{name}</Text>
              <Text style={styles.listSubtitleText}>{isShared ? 'Shared with me' : 'My workbook'}</Text>
            </View>

            {/* Chapters Count Badge */}
            <View style={{flex: 1.0, justifyContent: 'center', alignItems: 'center'}}>
              <View style={styles.listChaptersBadge}>
                <Text style={styles.listChaptersText}>
                  {chaptersCount ?? 0} { (chaptersCount ?? 0) === 1 ? 'chapter' : 'chapters' }
                </Text>
              </View>
            </View>

            {/* Platform / Source */}
            <View style={{flex: 1.0, justifyContent: 'center', alignItems: 'center'}}>
              <View style={styles.listPlatformContainer}>
                {platform === 'lichess' ? (
                  <>
                    <img src={LichessLogo} style={{width: 15, height: 15, objectFit: 'contain'}} alt="Lichess" />
                    <Text style={styles.listPlatformText}>Lichess</Text>
                  </>
                ) : (
                  <>
                    <img src={CCLogo} style={{width: 15, height: 15, objectFit: 'contain'}} alt="CircleChess" />
                    <Text style={styles.listPlatformText}>CircleChess</Text>
                  </>
                )}
              </View>
            </View>

            {/* Visibility Badge */}
            <View style={{flex: 1.0, justifyContent: 'center', alignItems: 'center'}}>
              <View style={styles.listVisibilityContainer}>
                <MaterialCommunityIcons
                  name={visibility === 'private' ? 'lock-outline' : 'earth'}
                  size={14}
                  color="#4E3E34"
                />
                <Text style={styles.listVisibilityText}>
                  {visibility === 'private' ? 'Private' : 'Public'}
                </Text>
              </View>
            </View>

            {/* Date Accessed */}
            <View style={{flex: 1.2, justifyContent: 'center', alignItems: 'center'}}>
              <View style={styles.listDateContainer}>
                <MaterialCommunityIcons name="calendar-month-outline" size={13} color="#757575" />
                <Text style={styles.listDateText}>{formatRelativeTime(createdAt)}</Text>
              </View>
            </View>

            {/* Action Buttons */}
            <View style={{flex: 1.2, justifyContent: 'center', alignItems: 'center'}}>
              <View style={styles.listActionsContainer}>
                {/* combined Edit & Share button */}
                {onEdit && onShare ? (
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                    style={[
                      styles.listActionButton,
                      {
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: isCombinedHovered ? 'rgba(78,62,52,0.06)' : 'transparent',
                        paddingHorizontal: 8,
                        paddingVertical: 5,
                        gap: 6,
                        borderWidth: 1,
                        borderColor: '#EAE2D5',
                      },
                    ]}
                    // @ts-ignore
                    onMouseEnter={() => setIsCombinedHovered(true)}
                    onMouseLeave={() => setIsCombinedHovered(false)}>
                    <MaterialCommunityIcons
                      name="pencil-outline"
                      size={14}
                      color={isCombinedHovered ? '#A5792F' : '#4E3E34'}
                    />
                    <View style={{width: 1, height: 10, backgroundColor: '#EAE2D5'}} />
                    <MaterialCommunityIcons
                      name="share-variant-outline"
                      size={14}
                      color={isCombinedHovered ? '#A5792F' : '#A39081'}
                    />
                  </TouchableOpacity>
                ) : (
                  onEdit && (
                    <TouchableOpacity
                      onPress={(e) => { e.stopPropagation(); onEdit(); }}
                      style={[styles.listActionButton, {backgroundColor: isEditHovered ? 'rgba(0,176,116,0.06)' : 'transparent'}]}
                      // @ts-ignore
                      onMouseEnter={() => setIsEditHovered(true)}
                      onMouseLeave={() => setIsEditHovered(false)}>
                      <MaterialCommunityIcons name="pencil-outline" size={14} color="#4E3E34" />
                    </TouchableOpacity>
                  )
                )}

                {/* Clone action button */}
                {onClone && (
                  <TouchableOpacity
                    onPress={(e) => {
                      e.stopPropagation();
                      setIsCloneConfirmVisible(true);
                      setIsDeleteConfirmVisible(false);
                    }}
                    style={[styles.listActionButton, {backgroundColor: isCloneHovered ? 'rgba(250,207,71,0.08)' : 'transparent'}]}
                    // @ts-ignore
                    onMouseEnter={() => setIsCloneHovered(true)}
                    onMouseLeave={() => setIsCloneHovered(false)}>
                    <MaterialCommunityIcons name="content-copy" size={14} color={isCloneHovered ? '#A5792F' : '#00B074'} />
                  </TouchableOpacity>
                )}

                {/* Delete action button */}
                {onDelete && (
                  <TouchableOpacity
                    onPress={(e) => {
                      e.stopPropagation();
                      setIsDeleteConfirmVisible(true);
                      setIsCloneConfirmVisible(false);
                    }}
                    style={[styles.listActionButton, {backgroundColor: isDeleteHovered ? 'rgba(211,47,47,0.06)' : 'transparent'}]}
                    // @ts-ignore
                    onMouseEnter={() => setIsDeleteHovered(true)}
                    onMouseLeave={() => setIsDeleteHovered(false)}>
                    <MaterialCommunityIcons name="trash-can-outline" size={14} color="#D32F2F" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </>
        )}
      </TouchableOpacity>

      {/* Expanded Delete Confirmation Drawer */}
      {isDeleteConfirmVisible && (
        <View
          // @ts-ignore
          onClick={(e: any) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={styles.listConfirmDrawer}>
          <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12}}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 240}}>
              <MaterialCommunityIcons name="alert-outline" size={20} color="#D32F2F" />
              <View>
                <Text style={{fontSize: 13, fontWeight: 'bold', color: '#1E110A'}}>Delete workbook?</Text>
                <Text style={{fontSize: 11, color: '#757575', marginTop: 1}}>All chapters and moves are permanently removed.</Text>
              </View>
            </View>
            <View style={{flexDirection: 'row', gap: 8}}>
              <TouchableOpacity
                onPress={() => setIsDeleteConfirmVisible(false)}
                style={{
                  borderWidth: 1.5,
                  borderColor: '#E6E6E6',
                  backgroundColor: '#FFFFFF',
                  borderRadius: 100,
                  paddingHorizontal: 14,
                  paddingVertical: 5,
                }}>
                <Text style={{fontSize: 11, fontWeight: 'bold', color: '#4E3E34'}}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  onDelete?.();
                  setIsDeleteConfirmVisible(false);
                }}
                style={{
                  backgroundColor: '#D32F2F',
                  borderRadius: 100,
                  paddingHorizontal: 16,
                  paddingVertical: 5,
                }}>
                <Text style={{fontSize: 11, fontWeight: 'bold', color: '#FFFFFF'}}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Expanded Clone Confirmation Drawer */}
      {isCloneConfirmVisible && (
        <View
          // @ts-ignore
          onClick={(e: any) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={styles.listConfirmDrawer}>
          <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12}}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 280}}>
              <MaterialCommunityIcons name="content-copy" size={20} color="#A5792F" />
              <View style={{flex: 1, gap: 4}}>
                <Text style={{fontSize: 13, fontWeight: 'bold', color: '#1E110A'}}>Clone Workbook</Text>
                <TextInput
                  value={cloneName}
                  onChangeText={setCloneName}
                  placeholder="Workbook clone name"
                  placeholderTextColor="#A39081"
                  style={{
                    height: 32,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#E6E6E6',
                    backgroundColor: '#FFFFFF',
                    paddingHorizontal: 10,
                    fontSize: 11.5,
                    color: '#1E110A',
                    width: '100%',
                    maxWidth: 320,
                  }}
                />
                <Text style={{fontSize: 10, color: '#757575'}}>
                  Copies all {chaptersCount ?? 0} chapters.
                </Text>
              </View>
            </View>
            <View style={{flexDirection: 'row', gap: 8, alignSelf: 'flex-end'}}>
              <TouchableOpacity
                onPress={() => setIsCloneConfirmVisible(false)}
                style={{
                  borderWidth: 1.5,
                  borderColor: '#E6E6E6',
                  backgroundColor: '#FFFFFF',
                  borderRadius: 100,
                  paddingHorizontal: 14,
                  paddingVertical: 5,
                }}>
                <Text style={{fontSize: 11, fontWeight: 'bold', color: '#4E3E34'}}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  onClone?.(cloneName);
                  setIsCloneConfirmVisible(false);
                }}
                style={{
                  backgroundColor: '#FACF47',
                  borderRadius: 100,
                  paddingHorizontal: 16,
                  paddingVertical: 5,
                }}>
                <Text style={{fontSize: 11, fontWeight: 'bold', color: '#1E110A'}}>Clone</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WorkbookGrid — Redesigned as List Table
// ─────────────────────────────────────────────────────────────────────────────
const WorkbookGrid = ({
  rows,
  emptyTitle,
  emptyDescription,
  showCreateAction,
  isShared,
  onOpen,
  onCreate,
  onDelete,
  onShare,
  onClone,
  onEdit,
}: {
  rows: {
    name: string;
    owner?: string;
    createdAt?: string;
    chapters?: string[];
    slug: string;
    chaptersCount: number;
    visibility?: string;
    platform?: string;
  }[];
  emptyTitle: string;
  emptyDescription: string;
  showCreateAction: boolean;
  isShared: boolean;
  onOpen: (slug: string) => void;
  onCreate: () => void;
  onDelete?: (slug: string) => void;
  onShare?: (slug: string, visibility?: string) => void;
  onClone?: (slug: string, name?: string) => void;
  onEdit?: (slug: string) => void;
}) => {
  const {isMobileOrTab} = useMediaQuery();

  if (rows.length === 0) {
    return (
      <View style={{width: '100%'}}>
        <View style={styles.emptyCard}>
          <Empty
            imgSrc={WorkbookEmptyBook}
            title={emptyTitle}
            description={emptyDescription}>
            {showCreateAction && (
              <Box type="row-center" style={{marginTop: 20}}>
                <CCButton style={styles.heroCreateButton} onPress={onCreate}>
                  <Box type="row-center" style={{gap: 8}}>
                    <MaterialCommunityIcons
                      name="plus-circle"
                      size={15}
                      color={CCColors.Primary.Dark}
                    />
                    <CCText
                      fontSize={FontSizes[13]}
                      fontWeight="semibold"
                      color={CCColors.Primary.Dark}>
                      Create first workbook
                    </CCText>
                  </Box>
                </CCButton>
              </Box>
            )}
          </Empty>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.listContainer}>
      {/* Table Header Row (Desktop only) */}
      {!isMobileOrTab && (
        <View style={styles.listHeaderRow}>
          <View style={{width: 40, justifyContent: 'center', alignItems: 'center'}}>
            <Text style={styles.listHeaderCell}>#</Text>
          </View>
          <View style={{flex: 2.2, justifyContent: 'center'}}>
            <Text style={styles.listHeaderCell}>Workbook Name</Text>
          </View>
          <View style={{flex: 1.0, justifyContent: 'center', alignItems: 'center'}}>
            <Text style={styles.listHeaderCell}>Chapters</Text>
          </View>
          <View style={{flex: 1.0, justifyContent: 'center', alignItems: 'center'}}>
            <Text style={styles.listHeaderCell}>Source</Text>
          </View>
          <View style={{flex: 1.0, justifyContent: 'center', alignItems: 'center'}}>
            <Text style={styles.listHeaderCell}>Visibility</Text>
          </View>
          <View style={{flex: 1.2, justifyContent: 'center', alignItems: 'center'}}>
            <Text style={styles.listHeaderCell}>Date Accessed</Text>
          </View>
          <View style={{flex: 1.2, justifyContent: 'center', alignItems: 'center'}}>
            <Text style={styles.listHeaderCell}>Actions</Text>
          </View>
        </View>
      )}

      {/* Row List */}
      <View style={{flexDirection: 'column'}}>
        {rows.map((row, idx) => (
          <WorkbookRow
            key={row.slug}
            name={row.name}
            owner={row.owner}
            createdAt={row.createdAt}
            chaptersCount={row.chaptersCount}
            visibility={row.visibility}
            platform={row.platform}
            index={idx}
            isShared={isShared}
            onPress={() => onOpen(row.slug)}
            onDelete={onDelete ? () => onDelete(row.slug) : undefined}
            onShare={onShare ? () => onShare(row.slug, row.visibility) : undefined}
            onClone={onClone ? (cloneName) => onClone(row.slug, cloneName) : undefined}
            onEdit={onEdit ? () => onEdit(row.slug) : undefined}
          />
        ))}
      </View>
    </View>
  );
};

// Custom date formatter to output exactly like "3rd June 2026" without time
const formatStudyDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';

    const day = date.getDate();
    let suffix = 'th';
    if (day % 10 === 1 && day !== 11) suffix = 'st';
    else if (day % 10 === 2 && day !== 12) suffix = 'nd';
    else if (day % 10 === 3 && day !== 13) suffix = 'rd';

    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthName = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}${suffix} ${monthName} ${year}`;
  } catch {
    return '—';
  }
};

// Custom relative date formatter (e.g. "3 days ago", "Yesterday")
const formatRelativeTime = (dateStr: string | undefined): string => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays < 1) {
      if (diffHours < 1) {
        if (diffMinutes < 1) {
          return 'Just now';
        }
        return `${diffMinutes}m ago`;
      }
      return `${diffHours}h ago`;
    }
    
    if (diffDays === 1) {
      return 'Yesterday';
    }
    
    if (diffDays < 30) {
      return `${diffDays} days ago`;
    }
    
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
      return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
    }
    
    const diffYears = Math.floor(diffDays / 365);
    return `${diffYears} ${diffYears === 1 ? 'year' : 'years'} ago`;
  } catch {
    return '—';
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// StudyGameList — main screen (all data logic unchanged)
// ─────────────────────────────────────────────────────────────────────────────
function StudyGameList() {
  const [tabSelected, setTabSelected] = useState(0);
  const [openNewStudyModal, setOpenNewStudyModal] = useState(false);
  // One merged "Workbook Settings" modal replaces the old separate Share
  // and Edit modals - both row icons open it (they duplicated visibility +
  // participants almost entirely).
  const [settingsModalRow, setSettingsModalRow] = useState<{
    slug: string;
    name?: string;
    visibility?: string;
    isOwnerTab: boolean;
  } | null>(null);
  const [editName, setEditName] = useState('');
  const [filter, setFilter] = useState<Tab>('owner');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedVisibility, setSelectedVisibility] = useState<'all' | 'public' | 'private'>('all');
  const [selectedPlatform, setSelectedPlatform] = useState<'all' | 'circlechess' | 'lichess'>('all');
  const [selectedChapterStatus, setSelectedChapterStatus] = useState<'all' | 'has_chapters' | 'empty'>('all');
  const {isMobileOrTab} = useMediaQuery();
  const navigation = useNavigation();
  const {data, isLoading, refetch} = useGetAllStudyGames(filter, () => {});
  const showToast = useToast();

  const [activeStudyDetail, setActiveStudyDetail] = useState<any>(null);
  const [activeStudyLoading, setActiveStudyLoading] = useState(false);
  const [newParticipantPhone, setNewParticipantPhone] = useState('');
  const [newParticipantRole, setNewParticipantRole] = useState<'contributor' | 'viewer'>('contributor');
  const [participantError, setParticipantError] = useState({flag: false, msg: ''});
  const [playersPage, setPlayersPage] = useState(0);

  const fetchActiveStudyDetail = async (slug: string) => {
    try {
      setActiveStudyLoading(true);
      const res = await ChessRestClient.get(`/learning/study-meta/${slug}/`);
      if (res?.data?.success) {
        setActiveStudyDetail(res.data.data);
      }
    } catch (e) {
      console.error('Failed to fetch study details:', e);
      showToast('Failed to fetch study details', {type: 'Error'});
    } finally {
      setActiveStudyLoading(false);
    }
  };

  const openWorkbookSettings = (
    slug: string,
    {
      name,
      visibility,
      isOwnerTab = true,
    }: {name?: string; visibility?: string; isOwnerTab?: boolean},
  ) => {
    setSettingsModalRow({slug, name, visibility, isOwnerTab});
    setEditName(name || '');
    setActiveStudyDetail(null);
    setNewParticipantPhone('');
    setParticipantError({flag: false, msg: ''});
    setPlayersPage(0);
    fetchActiveStudyDetail(slug);
  };

  const {mutate: renameStudy, isLoading: isRenamingStudy} = useRenameStudy(
    () => {
      refetch();
      showToast('Workbook renamed successfully', {type: 'Success'});
      setSettingsModalRow(null);
    },
    (err: any) => {
      const msg = err?.response?.data?.error || 'Failed to rename workbook.';
      showToast(msg, {type: 'Error'});
    }
  );

  const {mutate: updateVisibility, isLoading: isUpdatingVisibility} = useUpdateVisibility(
    () => {
      refetch();
      showToast('Visibility updated', {type: 'Success'});
    },
    () => showToast('Failed to update visibility', {type: 'Error'}),
  );

  const handleSetVisibility = (nextVisibility: 'public' | 'private') => {
    if (!settingsModalRow || settingsModalRow.visibility === nextVisibility) {
      return;
    }
    updateVisibility(
      {gameId: settingsModalRow.slug, visibility: nextVisibility},
      {
        onSuccess: () => {
          setSettingsModalRow(prev => (prev ? {...prev, visibility: nextVisibility} : prev));
          setActiveStudyDetail(prev => prev ? {...prev, visibility: nextVisibility} : prev);
        },
      } as any,
    );
  };

  const {mutate: manageParticipants, isLoading: isManagingParticipants} = useManageParticipants(
    (data: any) => {
      showToast(data?.message || 'Participant updated successfully', {type: 'Success'});
      setNewParticipantPhone('');
      setParticipantError({flag: false, msg: ''});
      if (settingsModalRow) fetchActiveStudyDetail(settingsModalRow.slug);
    },
    (error: any) => {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        'Failed to update participant. Check the number and try again.';
      setParticipantError({flag: true, msg});
      showToast(msg, {type: 'Error'});
    },
  );

  const {mutate: updateParticipantRole} = useUpdateParticipantRole(
    () => {
      showToast('Role updated successfully', {type: 'Success'});
      if (settingsModalRow) fetchActiveStudyDetail(settingsModalRow.slug);
    },
    () => showToast('Failed to update role', {type: 'Error'}),
  );

  const handleAddParticipant = (slug: string) => {
    const trimmed = newParticipantPhone.trim();
    if (!trimmed) {
      setParticipantError({flag: true, msg: 'Please enter a mobile number.'});
      return;
    }

    // Normalize phone number: strip non-digits, prepend '91' if 10-digits
    let phoneVal = trimmed.replace(/\D/g, '');
    if (phoneVal.length === 10) {
      phoneVal = '91' + phoneVal;
    } else if (phoneVal.length > 10 && phoneVal.startsWith('0')) {
      phoneVal = '91' + phoneVal.slice(1);
    }

    if (!phoneVal) {
      setParticipantError({flag: true, msg: 'Please enter a valid mobile number.'});
      return;
    }

    setParticipantError({flag: false, msg: ''});
    manageParticipants({
      phone: phoneVal,
      action: 'add',
      gameId: slug,
      role: newParticipantRole,
    });
  };

  const handleRemoveParticipant = (slug: string, phone: string) => {
    manageParticipants({
      phone,
      action: 'remove',
      gameId: slug,
    });
  };

  const handleRoleChange = (slug: string, phone: string, role: 'contributor' | 'viewer') => {
    updateParticipantRole({gameId: slug, phone, role});
  };

  const handleCopyShareLink = (slug: string) => {
    const link = `${window.location.origin}/studyDetails?study_id=${slug}`;
    Clipboard.setString(link);
    showToast('Study link copied to clipboard', {type: 'Success'});
  };

  const {mutate: cloneStudyGame} = useCloneStudyGame(
    (response: any) => {
      refetch();
      showToast('Workbook cloned successfully', {type: 'Success'});
      const newSlug = response?.data?.slug;
      if (newSlug) goToStudy(newSlug);
    },
    () => showToast('Failed to clone workbook', {type: 'Error'}),
  );

  const handleCloneStudy = (slug: string, name?: string) => {
    cloneStudyGame({gameId: slug, name});
  };

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [lichessPickerOpen, setLichessPickerOpen] = useState(false);

  // Give up polling if the backend has been stuck IN_PROGRESS this long
  // (e.g. the Celery worker died) — the redis progress key expires
  // server-side too, this just stops the client spinner sooner.
  const SYNC_POLL_TIMEOUT_MS = 10 * 60 * 1000;
  const pollTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pollStartedAtRef = React.useRef(0);
  const pollErrorCountRef = React.useRef(0);

  // Clear any pending poll timer on unmount so the loop doesn't keep
  // setState-ing an unmounted screen.
  React.useEffect(
    () => () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    },
    [],
  );

  const stopSyncUi = () => {
    setIsSyncing(false);
    setSyncStatus('');
  };

  const syncStudiesMutation = useSyncStudies(
    () => {
      pollStartedAtRef.current = Date.now();
      pollErrorCountRef.current = 0;
      pollSyncProgress();
    },
    (error: any) => {
      console.error('Error triggering sync:', error);
      stopSyncUi();
      // Alert.alert doesn't work on React Native Web — use toast
      showToast(
        error?.response?.data?.error || 'Failed to start study sync.',
        {type: 'Error'},
      );
    },
  );

  const progressMutation = useSyncStudiesProgress(
    progressResponse => {
      pollErrorCountRef.current = 0;
      if (progressResponse?.status === 'COMPLETED') {
        stopSyncUi();
        const failed = progressResponse?.data?.failed_studies ?? [];
        if (failed.length > 0) {
          const preview = failed.slice(0, 3).join(', ');
          showToast(
            `Sync finished, but ${failed.length} ${
              failed.length === 1 ? 'study' : 'studies'
            } could not be imported: ${preview}${
              failed.length > 3 ? '…' : ''
            }`,
            {type: 'Error'},
          );
        }
        refetch();
      } else if (progressResponse?.status === 'IN_PROGRESS') {
        if (Date.now() - pollStartedAtRef.current > SYNC_POLL_TIMEOUT_MS) {
          stopSyncUi();
          showToast(
            'The Lichess sync is taking longer than expected. Your studies will keep importing in the background — refresh in a bit.',
            {type: 'Error'},
          );
          return;
        }
        const imported = progressResponse?.data?.imported_studies || 0;
        const total = progressResponse?.data?.total_studies || 0;
        setSyncStatus(`Syncing: ${imported}/${total} studies`);
        pollTimeoutRef.current = setTimeout(pollSyncProgress, 1000);
      }
    },
    error => {
      console.error('Error checking progress:', error);
      // Tolerate transient failures (network blip) before giving up —
      // previously a single failed poll froze the UI at "Syncing…" forever.
      pollErrorCountRef.current += 1;
      if (pollErrorCountRef.current < 3) {
        pollTimeoutRef.current = setTimeout(pollSyncProgress, 2000);
        return;
      }
      stopSyncUi();
      showToast(
        'Lost track of sync progress. Refresh shortly to see your imported studies.',
        {type: 'Error'},
      );
    },
  );

  const pollSyncProgress = () => progressMutation.mutate();

  // ── Handle return from Lichess OAuth redirect ────────────────────────────
  // When Lichess redirects back here it appends ?lichess_auth=success (or error).
  // Detect it on mount, clean the URL, and auto-start the sync.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const lichessAuth = params.get('lichess_auth');
    if (!lichessAuth) return;

    // Strip the param from the URL so a page refresh doesn't re-trigger
    params.delete('lichess_auth');
    params.delete('reason');
    const newSearch = params.toString();
    const newUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : '');
    window.history.replaceState({}, '', newUrl);

    if (lichessAuth === 'success') {
      // The backend no longer auto-imports the whole account after OAuth
      // (Lichess rate-limits that) — open the picker so the user chooses
      // which studies to import.
      showToast('Lichess connected! Choose which studies to import.', {
        type: 'Success',
      });
      setLichessPickerOpen(true);
    } else {
      showToast('Lichess authorization failed. Please try again.', {type: 'Error'});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {refetch: checkLichessAuth} = useLichessAuthStatus();

  const handleSyncLichess = async () => {
    try {
      const detailsStr = await getUserDetails();
      const details = detailsStr ? JSON.parse(detailsStr) : null;
      if (!details || !details.lic_name) {
        // Alert was never imported here (and Alert.alert is a no-op on RN
        // Web anyway) — this path used to crash with a ReferenceError.
        showToast('Please add your Lichess username in Settings first.', {
          type: 'Error',
        });
        return;
      }

      // Check if the user already has an OAuth token stored
      const authResult = await checkLichessAuth();
      const hasToken = authResult?.data?.has_token ?? false;

      if (!hasToken) {
        // No token yet — kick off the OAuth PKCE flow.
        // The initiate endpoint is authenticated (JWT via Authorization header),
        // so we call it through ChessRestClient and get back the Lichess URL.
        setSyncStatus('Connecting to Lichess...');
        try {
          const initiateRes = await ChessRestClient.get(
            '/learning/lichess/oauth/initiate/',
          );
          const oauthUrl = initiateRes?.data?.oauth_url;
          if (oauthUrl) {
            // Full-page redirect to Lichess authorization screen
            window.location.href = oauthUrl;
          } else {
            showToast('Could not start Lichess authorization. Please try again.', {
              type: 'Error',
            });
          }
        } catch (e: any) {
          console.error('OAuth initiate error:', e);
          showToast(
            e?.response?.data?.error || 'Failed to start Lichess authorization.',
            {type: 'Error'},
          );
        }
        setSyncStatus('');
        return;
      }

      // Token exists — let the user pick which studies to import instead of
      // pulling the whole account (Lichess rate-limits heavy API usage).
      setLichessPickerOpen(true);
    } catch (e) {
      console.error('Sync error:', e);
      setIsSyncing(false);
    }
  };

  const handleImportSelectedStudies = (studyIds: string[]) => {
    setLichessPickerOpen(false);
    setIsSyncing(true);
    setSyncStatus('Starting Lichess import...');
    syncStudiesMutation.mutate({studyIds});
  };

  const deleteStudyMutation = useDeleteStudy(
    () => {
      refetch();
    },
    (error: any) => {
      console.error('Error deleting study:', error);
      showToast(
        error?.response?.data?.error || 'Failed to delete workbook.',
        {type: 'Error'},
      );
    },
  );

  const handleDeleteStudy = (slug: string) => {
    deleteStudyMutation.mutate(slug);
  };

  const isAnyFilterActive =
    selectedVisibility !== 'all' ||
    selectedPlatform !== 'all' ||
    selectedChapterStatus !== 'all';

  const handleClearAllFilters = () => {
    setSelectedVisibility('all');
    setSelectedPlatform('all');
    setSelectedChapterStatus('all');
  };

  const rows = useMemo(
    () =>
      (data?.data ?? [])
        .map(eachGame => ({
          name: eachGame?.name,
          owner: eachGame?.owner_details?.username,
          createdAt: eachGame?.created_at,
          lastOpenedAt: eachGame?.last_opened_at,
          chapters: (eachGame?.chapters ?? []).map(
            (ch: any) => ch?.name ?? ch?.title ?? '',
          ),
          slug: eachGame?.slug,
          chaptersCount: eachGame?.chapters_count ?? 0,
          visibility: eachGame?.visibility,
          platform: eachGame?.platform,
        }))
        .sort((a, b) => {
          const timeA = new Date(a.lastOpenedAt || a.createdAt || 0).getTime();
          const timeB = new Date(b.lastOpenedAt || b.createdAt || 0).getTime();
          return timeB - timeA;
        }),
    [data],
  );

  const filteredRows = useMemo(() => {
    let result = rows;

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter(
        row =>
          row.name?.toLowerCase().includes(query) ||
          row.owner?.toLowerCase().includes(query) ||
          row.chapters?.some((ch: string) => ch.toLowerCase().includes(query)),
      );
    }

    if (selectedVisibility !== 'all') {
      result = result.filter(row => row.visibility === selectedVisibility);
    }

    if (selectedPlatform !== 'all') {
      result = result.filter(row => row.platform === selectedPlatform);
    }

    if (selectedChapterStatus !== 'all') {
      if (selectedChapterStatus === 'has_chapters') {
        result = result.filter(row => row.chaptersCount > 0);
      } else if (selectedChapterStatus === 'empty') {
        result = result.filter(row => row.chaptersCount === 0);
      }
    }

    return result;
  }, [rows, searchQuery, selectedVisibility, selectedPlatform, selectedChapterStatus]);

  const renderParticipantsSection = (slug: string) => {
    return (
      <View style={{ gap: 12 }}>
        <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>PLAYERS</Text>
        
        {activeStudyLoading ? (
          <Text style={{ fontSize: 11.5, color: '#757575', fontStyle: 'italic' }}>
            Loading participants...
          </Text>
        ) : (
          <View style={{ gap: 10 }}>
            {/* List current participants */}
            <View style={{ gap: 6, maxHeight: 180, overflowY: 'auto' } as any}>
              <View
                key="owner"
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingVertical: 6,
                  paddingHorizontal: 8,
                  backgroundColor: '#FFFDF6',
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: '#EAE2D5',
                }}>
                <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 13,
                      backgroundColor: '#402C1F',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Text style={{fontSize: 11, fontWeight: 'bold', color: '#FFFFFF'}}>
                      {activeStudyDetail?.owner_details?.username ? activeStudyDetail.owner_details.username.charAt(0).toUpperCase() : 'O'}
                    </Text>
                  </View>
                  <Text style={{ fontSize: 12, fontWeight: 'semibold', color: '#1E110A' }}>
                    {activeStudyDetail?.owner_details?.username || 'Owner'} (You)
                  </Text>
                  {activeStudyDetail?.owner_details?.mobile_number && (
                    <Text style={{ fontSize: 10.5, color: '#757575' }}>
                      ({activeStudyDetail.owner_details.mobile_number})
                    </Text>
                  )}
                </View>
                <View style={{ backgroundColor: '#FFF4E3', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 100 }}>
                  <Text style={{ fontSize: 9.5, fontWeight: 'bold', color: '#A5792F' }}>Owner</Text>
                </View>
              </View>

              {activeStudyDetail?.participants && activeStudyDetail.participants.map((player: any) => {
                return (
                  <View
                    key={player.mobile_number || player.username}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingVertical: 6,
                      paddingHorizontal: 8,
                      backgroundColor: '#FFFFFF',
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: '#E6E6E6',
                    }}>
                    <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <View
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 13,
                          backgroundColor: '#402C1F',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <Text style={{fontSize: 11, fontWeight: 'bold', color: '#FFFFFF'}}>
                          {player.username ? player.username.charAt(0).toUpperCase() : 'P'}
                        </Text>
                      </View>
                      <Text style={{ fontSize: 12, fontWeight: 'medium', color: '#1E110A' }}>
                        {player.username}
                      </Text>
                      {player.mobile_number && (
                        <Text style={{ fontSize: 10.5, color: '#757575' }}>
                          ({player.mobile_number})
                        </Text>
                      )}
                    </View>
                    
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      {/* Contributor / Viewer selector */}
                      <View style={{ flexDirection: 'row', backgroundColor: '#F5F5F5', borderRadius: 6, padding: 2 }}>
                        <TouchableOpacity
                          onPress={() => handleRoleChange(slug, player.mobile_number, 'contributor')}
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 3,
                            borderRadius: 4,
                            backgroundColor: player.role === 'contributor' ? '#402C1F' : 'transparent',
                          }}>
                          <Text style={{ fontSize: 9.5, fontWeight: 'bold', color: player.role === 'contributor' ? '#FFFFFF' : '#757575' }}>
                            Contributor
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => handleRoleChange(slug, player.mobile_number, 'viewer')}
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 3,
                            borderRadius: 4,
                            backgroundColor: player.role === 'viewer' ? '#402C1F' : 'transparent',
                          }}>
                          <Text style={{ fontSize: 9.5, fontWeight: 'bold', color: player.role === 'viewer' ? '#FFFFFF' : '#757575' }}>
                            Viewer
                          </Text>
                        </TouchableOpacity>
                      </View>
                      
                      {/* Remove button */}
                      <TouchableOpacity
                        onPress={() => handleRemoveParticipant(slug, player.mobile_number)}
                        disabled={isManagingParticipants}
                        style={{ padding: 4 }}>
                        <MaterialCommunityIcons name="trash-can-outline" size={14} color="#D32F2F" />
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </View>
            
            {/* Add participant form */}
            <View style={{ borderTopWidth: 1, borderColor: '#E6E6E6', paddingTop: 10, marginTop: 4, gap: 8 }}>
              <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#1E110A' }}>
                Share with another user
              </Text>
              <TextInput
                value={newParticipantPhone}
                onChangeText={(val) => {
                  setNewParticipantPhone(val);
                  setParticipantError({flag: false, msg: ''});
                }}
                placeholder="Enter mobile number"
                placeholderTextColor="#A39081"
                style={{
                  height: 38,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: participantError?.flag ? '#FF4D4D' : '#E6E6E6',
                  backgroundColor: '#FFFFFF',
                  paddingHorizontal: 12,
                  fontSize: 12.5,
                  color: '#1E110A',
                }}
              />
              {participantError?.flag && (
                <Text style={{fontSize: 9.5, color: '#FF4D4D', fontWeight: '500'}}>{participantError.msg}</Text>
              )}
              
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: 11, color: '#757575' }}>Role:</Text>
                  <View style={{ flexDirection: 'row', backgroundColor: '#F5F5F5', borderRadius: 6, padding: 2 }}>
                    <TouchableOpacity
                      onPress={() => setNewParticipantRole('contributor')}
                      style={{
                        paddingHorizontal: 10,
                        paddingVertical: 4,
                        borderRadius: 4,
                        backgroundColor: newParticipantRole === 'contributor' ? '#402C1F' : 'transparent',
                      }}>
                      <Text style={{ fontSize: 10, fontWeight: 'bold', color: newParticipantRole === 'contributor' ? '#FFFFFF' : '#757575' }}>
                        Contributor
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => setNewParticipantRole('viewer')}
                      style={{
                        paddingHorizontal: 10,
                        paddingVertical: 4,
                        borderRadius: 4,
                        backgroundColor: newParticipantRole === 'viewer' ? '#402C1F' : 'transparent',
                      }}>
                      <Text style={{ fontSize: 10, fontWeight: 'bold', color: newParticipantRole === 'viewer' ? '#FFFFFF' : '#757575' }}>
                        Viewer
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
                
                <TouchableOpacity
                  onPress={() => handleAddParticipant(slug)}
                  disabled={isManagingParticipants || !newParticipantPhone}
                  style={{
                    backgroundColor: '#FACF47',
                    paddingVertical: 6,
                    paddingHorizontal: 16,
                    borderRadius: 100,
                    opacity: (isManagingParticipants || !newParticipantPhone) ? 0.6 : 1,
                  }}>
                  <Text style={{ fontSize: 11.5, color: '#1E110A', fontWeight: 'bold' }}>
                    {isManagingParticipants ? 'Adding…' : 'Add User'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}
      </View>
    );
  };

  const goToStudy = (slug: string) =>
    navigation?.navigate(ScreenName.StudyDetails, {study_id: slug});

  return (
    <View style={styles.container}>
      {/* ── Create study modal ────────────────────────────────────────── */}
      <CreateNewStudy
        isStudyModalOpen={openNewStudyModal}
        setIsStudyModalOpen={setOpenNewStudyModal}
        overrideButton={() => <></>}
      />

      {/* ── Lichess study picker modal ────────────────────────────────── */}
      <ImportLichessStudies
        open={lichessPickerOpen}
        onClose={() => setLichessPickerOpen(false)}
        onImport={handleImportSelectedStudies}
        isImporting={syncStudiesMutation.isLoading}
      />

      {/* ── Workbook Settings modal (merged Share + Edit) ─────────────── */}
      <SlideModal
        visible={!!settingsModalRow}
        onClose={() => setSettingsModalRow(null)}
        type="center"
        showHeader={false}
        contentPadding={0}
        customStyle={{
          borderRadius: 20,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          width: settingsModalRow?.isOwnerTab ? 780 : 480,
          maxWidth: '92%',
          alignSelf: 'center',
          borderWidth: 0,
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 10},
          shadowOpacity: 0.08,
          shadowRadius: 20,
          elevation: 5,
        }}>

        {/* Header Block */}
        <View
          style={{
            backgroundColor: '#2C1A11',
            paddingHorizontal: 24,
            paddingVertical: 18,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, marginRight: 24}}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: '#402C1F',
                borderWidth: 1,
                borderColor: '#B58E58',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <MaterialCommunityIcons name="chess-queen" size={18} color="#FFFFFF" />
            </View>
            <View style={{flex: 1}}>
              <Text style={{fontSize: 15, fontWeight: 'bold', color: '#FFFFFF', letterSpacing: -0.2}} numberOfLines={1}>
                {settingsModalRow?.name || 'Workbook Settings'}
              </Text>
              <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
                {settingsModalRow?.isOwnerTab
                  ? 'Name it, choose who can see it, invite your players.'
                  : 'Share this workbook with other players.'}
              </Text>
            </View>
          </View>

          {/* Close button */}
          <TouchableOpacity
            onPress={() => setSettingsModalRow(null)}
            activeOpacity={0.8}
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(255,255,255,0.08)',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Ionicons name="close-outline" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        {/* Body columns */}
        <View style={isMobileOrTab ? {padding: 24, gap: 18} : {flexDirection: 'row', width: '100%'}}>
          {/* Left Column: Settings */}
          <View style={isMobileOrTab ? {gap: 16} : {flex: 1.1, padding: 24, gap: 16}}>
            {settingsModalRow?.isOwnerTab && (
              <View style={{gap: 6}}>
                <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>WORKBOOK NAME</Text>
                <View style={{flexDirection: 'row', gap: 8}}>
                  <TextInput
                    value={editName}
                    onChangeText={setEditName}
                    placeholder="Enter workbook name"
                    style={{
                      flex: 1,
                      height: 38,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: '#E6E6E6',
                      backgroundColor: '#FFFFFF',
                      paddingHorizontal: 12,
                      fontSize: 12.5,
                      color: '#1E110A',
                    }}
                  />
                  <TouchableOpacity
                    onPress={() =>
                      settingsModalRow &&
                      renameStudy({gameId: settingsModalRow.slug, name: editName})
                    }
                    disabled={isRenamingStudy || !editName.trim()}
                    style={{
                      backgroundColor: '#FACF47',
                      justifyContent: 'center',
                      alignItems: 'center',
                      paddingHorizontal: 16,
                      borderRadius: 100,
                      height: 38,
                      opacity: isRenamingStudy || !editName.trim() ? 0.6 : 1,
                    }}>
                    <Text style={{fontSize: 12.5, fontWeight: 'bold', color: '#1E110A'}}>
                      {isRenamingStudy ? 'Saving…' : 'Save'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {settingsModalRow?.isOwnerTab && (
              <View style={{gap: 6}}>
                <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>VISIBILITY</Text>
                <View style={{flexDirection: 'row', gap: 8}}>
                  {(
                    [
                      {
                        key: 'private',
                        label: 'Private',
                        icon: 'lock-outline',
                        desc: 'Only you and invited players',
                      },
                      {
                        key: 'public',
                        label: 'Public',
                        icon: 'earth',
                        desc: 'Any signed-in user can view',
                      },
                    ] as const
                  ).map(opt => {
                    const isActive = (settingsModalRow?.visibility || 'private') === opt.key;
                    return (
                      <TouchableOpacity
                        key={opt.key}
                        disabled={isUpdatingVisibility}
                        onPress={() => handleSetVisibility(opt.key)}
                        style={{
                          flex: 1,
                          borderRadius: 10,
                          borderWidth: 1.5,
                          padding: 10,
                          gap: 3,
                          borderColor: isActive ? '#FACF47' : '#E6E6E6',
                          backgroundColor: isActive ? 'rgba(250,207,71,0.06)' : '#FFFFFF',
                          opacity: isUpdatingVisibility ? 0.6 : 1,
                        }}>
                        <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                          <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                            <MaterialCommunityIcons
                              name={opt.icon}
                              size={14}
                              color={isActive ? '#A5792F' : '#757575'}
                            />
                            <Text style={{fontSize: 12, fontWeight: 'bold', color: isActive ? '#A5792F' : '#4E3E34'}}>
                              {opt.label}
                            </Text>
                          </View>
                          {isActive && (
                            <MaterialCommunityIcons
                              name="check"
                              size={12}
                              color="#A5792F"
                            />
                          )}
                        </View>
                        <Text style={{fontSize: 10, color: '#757575', marginTop: 2}}>
                          {opt.desc}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            )}

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#FFFDF6',
                paddingVertical: 10,
                paddingHorizontal: 14,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: '#EAE2D5',
                marginVertical: 4,
              }}>
              <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                <MaterialCommunityIcons
                  name="link-variant"
                  size={16}
                  color="#4E3E34"
                />
                <Text style={{fontSize: 12.5, fontWeight: 'bold', color: '#1E110A'}}>
                  Invite Link
                </Text>
              </View>
              <TouchableOpacity
                onPress={() =>
                  settingsModalRow && handleCopyShareLink(settingsModalRow.slug)
                }
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 5,
                  backgroundColor: '#2C1A11',
                  paddingVertical: 5,
                  paddingHorizontal: 12,
                  borderRadius: 100,
                }}>
                <MaterialCommunityIcons
                  name="content-copy"
                  size={11}
                  color="#FFFFFF"
                />
                <Text style={{fontSize: 11, color: '#FFFFFF', fontWeight: 'bold'}}>
                  Copy
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {!isMobileOrTab && settingsModalRow?.isOwnerTab && (
            <View style={{width: 1, alignSelf: 'stretch', backgroundColor: '#E6E6E6'}} />
          )}

          {/* Right Column: Players */}
          {settingsModalRow && settingsModalRow.isOwnerTab && (
            <View style={isMobileOrTab ? {} : {flex: 1, padding: 24}}>
              {renderParticipantsSection(settingsModalRow.slug)}
            </View>
          )}
        </View>
      </SlideModal>

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}>

        {/* Hero */}
        <HeroHeader
          onCreate={() => setOpenNewStudyModal(true)}
          onSync={handleSyncLichess}
          workbookCount={rows.length}
        />

        {/* Body */}
        <View style={styles.bodyWrapper}>

          {/* Controls bar */}
          <View style={styles.controlsRow}>
            <SegmentedTabs
              tabSelected={tabSelected}
              onChange={index => {
                setTabSelected(index);
                setFilter(index === 1 ? 'participant' : 'owner');
                setSelectedVisibility('all');
                setSelectedPlatform('all');
                setSelectedChapterStatus('all');
              }}
            />

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                width: isMobileOrTab ? '100%' : 'auto',
              }}>
              <View
                style={[
                  styles.searchBox,
                  isMobileOrTab && {flex: 1, width: 'auto'},
                ]}>
                <MaterialCommunityIcons
                  name="magnify"
                  size={16}
                  color={CCColors.TextColor.Grey}
                />
                <TextInput
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholder="Search workbooks or chapters..."
                  placeholderTextColor={CCColors.TextColor.Grey}
                  style={styles.searchInput}
                />
              </View>
              
              <TouchableOpacity
                onPress={() => setIsFilterOpen(prev => !prev)}
                style={[
                  styles.filterButton,
                  isFilterOpen && {
                    borderColor: CCColors.Workbook.Brown,
                    backgroundColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.04),
                  },
                ]}>
                <MaterialCommunityIcons
                  name="filter-variant"
                  size={18}
                  color={isAnyFilterActive || isFilterOpen ? CCColors.Workbook.Brown : CCColors.TextColor.Grey}
                />
                {!isMobileOrTab && (
                  <CCText
                    fontSize={13}
                    fontWeight="medium"
                    color={isAnyFilterActive || isFilterOpen ? CCColors.Workbook.Brown : CCColors.TextColor.Dark}>
                    Filter
                  </CCText>
                )}
                {isAnyFilterActive && <View style={styles.filterIndicator} />}
              </TouchableOpacity>

              {!isMobileOrTab && searchQuery.length > 0 && (
                <CCText
                  fontSize={FontSizes[12]}
                  color={CCColors.TextColor.Grey}>
                  {filteredRows.length}{' '}
                  {filteredRows.length === 1 ? 'result' : 'results'}
                </CCText>
              )}
            </View>
          </View>

          {/* Collapsible Filter Panel */}
          {isFilterOpen && (
            <View style={[styles.filterPanel, isMobileOrTab && styles.filterPanelMobile]}>
              <View style={styles.filterPanelHeader}>
                <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                  <MaterialCommunityIcons
                    name="filter-outline"
                    size={16}
                    color={CCColors.Workbook.Brown}
                  />
                  <CCText fontSize={13} fontWeight="bold" color={CCColors.Workbook.Brown}>
                    Filter Workbooks
                  </CCText>
                </View>
                {isAnyFilterActive && (
                  <TouchableOpacity
                    onPress={handleClearAllFilters}
                    style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
                    <MaterialCommunityIcons
                      name="close-circle-outline"
                      size={14}
                      color={CCColors.Workbook.DangerRed}
                    />
                    <CCText fontSize={11} fontWeight="semibold" color={CCColors.Workbook.DangerRed}>
                      Clear All
                    </CCText>
                  </TouchableOpacity>
                )}
              </View>
              
              <View style={[styles.filterSectionsRow, isMobileOrTab && styles.filterSectionsRowMobile]}>
                {/* Section 1: Visibility */}
                <View style={styles.filterSection}>
                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8}}>
                    <MaterialCommunityIcons
                      name="eye-outline"
                      size={14}
                      color={CCColors.TextColor.Grey}
                    />
                    <CCText fontSize={10} fontWeight="bold" color={CCColors.TextColor.Grey} style={{letterSpacing: 0.5}}>
                      VISIBILITY
                    </CCText>
                  </View>
                  <View style={styles.filterChipsRow}>
                    {['all', 'public', 'private'].map(opt => (
                      <TouchableOpacity
                        key={opt}
                        onPress={() => setSelectedVisibility(opt as any)}
                        style={[
                          styles.filterChip,
                          selectedVisibility === opt && styles.filterChipActive,
                        ]}>
                        <View style={{flexDirection: 'row', alignItems: 'center'}}>
                          {selectedVisibility === opt && (
                            <MaterialCommunityIcons
                              name="check"
                              size={12}
                              color={CCColors.White}
                              style={{marginRight: 4}}
                            />
                          )}
                          <CCText
                            fontSize={11}
                            fontWeight={selectedVisibility === opt ? 'semibold' : 'medium'}
                            color={selectedVisibility === opt ? CCColors.White : CCColors.TextColor.Primary}>
                            {opt === 'all' ? 'All' : opt === 'public' ? 'Public' : 'Private'}
                          </CCText>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                {/* Section 2: Source */}
                <View style={styles.filterSection}>
                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8}}>
                    <MaterialCommunityIcons
                      name="database-outline"
                      size={14}
                      color={CCColors.TextColor.Grey}
                    />
                    <CCText fontSize={10} fontWeight="bold" color={CCColors.TextColor.Grey} style={{letterSpacing: 0.5}}>
                      SOURCE
                    </CCText>
                  </View>
                  <View style={styles.filterChipsRow}>
                    {['all', 'circlechess', 'lichess'].map(opt => (
                      <TouchableOpacity
                        key={opt}
                        onPress={() => setSelectedPlatform(opt as any)}
                        style={[
                          styles.filterChip,
                          selectedPlatform === opt && styles.filterChipActive,
                        ]}>
                        <View style={{flexDirection: 'row', alignItems: 'center'}}>
                          {selectedPlatform === opt && (
                            <MaterialCommunityIcons
                              name="check"
                              size={12}
                              color={CCColors.White}
                              style={{marginRight: 4}}
                            />
                          )}
                          <CCText
                            fontSize={11}
                            fontWeight={selectedPlatform === opt ? 'semibold' : 'medium'}
                            color={selectedPlatform === opt ? CCColors.White : CCColors.TextColor.Primary}>
                            {opt === 'all' ? 'All' : opt === 'circlechess' ? 'CircleChess' : 'Lichess'}
                          </CCText>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                {/* Section 3: Status */}
                <View style={styles.filterSection}>
                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8}}>
                    <MaterialCommunityIcons
                      name="format-list-bulleted"
                      size={14}
                      color={CCColors.TextColor.Grey}
                    />
                    <CCText fontSize={10} fontWeight="bold" color={CCColors.TextColor.Grey} style={{letterSpacing: 0.5}}>
                      CHAPTER STATUS
                    </CCText>
                  </View>
                  <View style={styles.filterChipsRow}>
                    {['all', 'has_chapters', 'empty'].map(opt => (
                      <TouchableOpacity
                        key={opt}
                        onPress={() => setSelectedChapterStatus(opt as any)}
                        style={[
                          styles.filterChip,
                          selectedChapterStatus === opt && styles.filterChipActive,
                        ]}>
                        <View style={{flexDirection: 'row', alignItems: 'center'}}>
                          {selectedChapterStatus === opt && (
                            <MaterialCommunityIcons
                              name="check"
                              size={12}
                              color={CCColors.White}
                              style={{marginRight: 4}}
                            />
                          )}
                          <CCText
                            fontSize={11}
                            fontWeight={selectedChapterStatus === opt ? 'semibold' : 'medium'}
                            color={selectedChapterStatus === opt ? CCColors.White : CCColors.TextColor.Primary}>
                            {opt === 'all' ? 'All' : opt === 'has_chapters' ? 'Has Chapters' : 'Empty'}
                          </CCText>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              </View>
            </View>
          )}

          {/* Section header */}
          <View style={styles.sectionHeader}>
            <View style={styles.sectionAccentLine} />
            <CCText
              fontSize={FontSizes[13]}
              fontWeight="semibold"
              color={CCColors.TextColor.Dark}>
              {tabSelected === 0 ? 'My Workbooks' : 'Shared with Me'}
            </CCText>
            {filteredRows.length > 0 && (
              <View style={styles.sectionCountBadge}>
                <CCText
                  fontSize={FontSizes[11]}
                  fontWeight="semibold"
                  color={CCColors.TextColor.Dark}>
                  {filteredRows.length}
                </CCText>
              </View>
            )}
          </View>

          {/* Grid */}
          {tabSelected === 0 ? (
            <WorkbookGrid
              rows={filteredRows}
              emptyTitle="No workbooks yet"
              emptyDescription="Create a workbook to start organizing your interactive training chapters."
              showCreateAction
              isShared={false}
              onOpen={goToStudy}
              onCreate={() => setOpenNewStudyModal(true)}
              onDelete={handleDeleteStudy}
              onShare={(slug, visibility) => {
                const found = rows.find(r => r.slug === slug);
                openWorkbookSettings(slug, {
                  name: found?.name,
                  visibility,
                  isOwnerTab: true,
                });
              }}
              onClone={handleCloneStudy}
              onEdit={(slug) => {
                const found = rows.find(r => r.slug === slug);
                if (found) {
                  openWorkbookSettings(slug, {
                    name: found.name,
                    visibility: found.visibility,
                    isOwnerTab: true,
                  });
                }
              }}
            />
          ) : (
            <WorkbookGrid
              rows={filteredRows}
              emptyTitle="No shared workbooks found"
              emptyDescription="Workbooks shared with you by other members will appear here."
              showCreateAction={false}
              isShared={true}
              onOpen={goToStudy}
              onCreate={() => setOpenNewStudyModal(true)}
              onDelete={handleDeleteStudy}
              onShare={(slug, visibility) => {
                const found = rows.find(r => r.slug === slug);
                openWorkbookSettings(slug, {
                  name: found?.name,
                  visibility,
                  isOwnerTab: false,
                });
              }}
              onClone={handleCloneStudy}
            />
          )}
        </View>
      </ScrollView>

      <CCLoader
        loading={isLoading || isSyncing}
        textToShow={isSyncing ? syncStatus : undefined}
      />
    </View>
  );
}

export default StudyGameList;
